import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import {
  Message as MessageIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  AutoFixHigh as AutoFixHighIcon,
  ChevronLeft,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

// Helper function to get initials from name
const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

// Helper function to get channel icon
const ChannelIcon = ({ channel }) => {
  switch (channel) {
    case "sms":
      return <MessageIcon />;
    case "email":
      return <MailIcon />;
    case "voice":
      return <PhoneIcon />;
    case "chat":
      return <ChatIcon />;
    default:
      return <MessageIcon />;
  }
};

const ConversationThreadMessages = ({ 
  selectedThread,
  messages = [],
  onBack,
  onSendMessage,
  onAssign,
  onMarkResolved,
  onSnooze,
  onViewContact,
  onAddNote,
  onAddToCampaign,
  onDeleteConversation,
}) => {
  const [replyText, setReplyText] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [channelAnchorEl, setChannelAnchorEl] = useState(null);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChannelClick = (event) => {
    setChannelAnchorEl(event.currentTarget);
  };

  const handleChannelClose = () => {
    setChannelAnchorEl(null);
  };

  const handleSendReply = () => {
    if (replyText.trim()) {
      onSendMessage(replyText);
      setReplyText("");
    }
  };

  if (!selectedThread) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" align="center">
          No thread selected
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ChevronLeft />}
            onClick={onBack}
            size="small"
          >
            Back to threads
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Assign">
              <IconButton onClick={() => onAssign(selectedThread)}>
                <PersonIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Mark as Resolved">
              <IconButton onClick={() => onMarkResolved(selectedThread)}>
                <CheckCircleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Snooze">
              <IconButton onClick={() => onSnooze(selectedThread)}>
                <AccessTimeIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleMenuClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => { onViewContact(selectedThread); handleMenuClose(); }}>
                View Contact
              </MenuItem>
              <MenuItem onClick={() => { onAddNote(selectedThread); handleMenuClose(); }}>
                Add Note
              </MenuItem>
              <MenuItem onClick={() => { onAddToCampaign(selectedThread); handleMenuClose(); }}>
                Add to Campaign
              </MenuItem>
              <Divider />
              <MenuItem 
                onClick={() => { onDeleteConversation(selectedThread); handleMenuClose(); }} 
                sx={{ color: 'error.main' }}
              >
                Delete Conversation
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: message.sender_type === 'user' ? 'flex-end' : 'flex-start',
              mb: 2,
            }}
          >
            <Paper
              sx={{
                p: 2,
                maxWidth: '70%',
                bgcolor: message.sender_type === 'user' ? 'primary.main' : 'background.paper',
                color: message.sender_type === 'user' ? 'primary.contrastText' : 'text.primary',
                border: message.sender_type === 'user' ? 'none' : '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Badge
                  variant="outline"
                  sx={{
                    fontSize: '0.75rem',
                    bgcolor: message.sender_type === 'user' ? 'primary.contrastText' : 'transparent',
                    color: message.sender_type === 'user' ? 'primary.main' : 'text.secondary',
                    borderColor: message.sender_type === 'user' ? 'primary.main' : 'divider',
                  }}
                >
                  <ChannelIcon channel={message.channel} />
                  <Typography variant="caption" sx={{ ml: 0.5, textTransform: 'capitalize' }}>
                    {message.channel || 'unknown'}
                  </Typography>
                </Badge>
                <Typography
                  variant="caption"
                  sx={{
                    color: message.sender_type === 'user' ? 'primary.contrastText' : 'text.secondary',
                    opacity: 0.7,
                  }}
                >
                  {message.timestamp ? new Date(message.timestamp).toLocaleString() : 'No timestamp'}
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: message.sender_type === 'user' ? 'primary.contrastText' : 'text.primary',
                }}
              >
                {message.content || 'No content'}
              </Typography>
              {message.media_url && (
                <Box sx={{ mt: 1 }}>
                  <img 
                    src={message.media_url} 
                    alt="Message attachment" 
                    style={{ maxWidth: '100%', borderRadius: 4 }}
                  />
                </Box>
              )}
            </Paper>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleChannelClick}
              startIcon={<ChannelIcon channel={selectedThread.channel || 'sms'} />}
            >
              {(selectedThread.channel || 'sms').charAt(0).toUpperCase() + (selectedThread.channel || 'sms').slice(1)}
            </Button>
            <Menu
              anchorEl={channelAnchorEl}
              open={Boolean(channelAnchorEl)}
              onClose={handleChannelClose}
            >
              <MenuItem onClick={() => { handleChannelClose(); }}>
                <MessageIcon sx={{ mr: 1 }} /> SMS
              </MenuItem>
              <MenuItem onClick={() => { handleChannelClose(); }}>
                <MailIcon sx={{ mr: 1 }} /> Email
              </MenuItem>
              <MenuItem onClick={() => { handleChannelClose(); }}>
                <PhoneIcon sx={{ mr: 1 }} /> Voice
              </MenuItem>
              <MenuItem onClick={() => { handleChannelClose(); }}>
                <ChatIcon sx={{ mr: 1 }} /> Chat
              </MenuItem>
            </Menu>
            <Button variant="outlined">Template</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Generate with AI">
              <IconButton>
                <AutoFixHighIcon />
              </IconButton>
            </Tooltip>
            <Button variant="contained" onClick={handleSendReply}>
              Send
            </Button>
          </Box>
        </Box>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Type your message..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
        />
      </Box>
    </Box>
  );
};

export default ConversationThreadMessages; 